import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import ManagerNav from "./managernav";
import StaffNav from "../staff/staffnav";
import { AuthContext } from "../AuthContext";

function StarIcon({ className }) {
  return (
    <svg
      className={className}
      fill="currentColor"
      viewBox="0 0 20 20"
      width="1em"
      height="1em"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.176 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" />
    </svg>
  );
}

export default function KnowledgeTree() {
  const [tasks, setTasks] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState("");
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const user = useContext(AuthContext);
  const navigate = useNavigate();

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    const access = String(user?.access || "").toLowerCase();
    navigate(access === "staff" ? "/staffdash" : "/dashboard");
  };

  useEffect(() => {
    const fetchTasksAndProcesses = async () => {
      try {
        const response = await fetch("https://api.insightos.co.uk/api/alltasks", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const processData = Array.isArray(data?.data) ? data.data : [];

        setTasks(processData);

        const uniqueProcesses = [...new Set(processData.map((task) => task.Name))].filter(Boolean);
        setProcesses(uniqueProcesses);
      } catch (error) {
        console.error(error);
      }
    };

    fetchTasksAndProcesses();
  }, []);

  useEffect(() => {
    const lowerSearchTerm = searchTerm.toLowerCase();

    const filtered = tasks.filter((process) => {
      if (selectedProcess && process.Name !== selectedProcess) {
        return false;
      }

      if (!lowerSearchTerm) {
        return true;
      }

      return (process.tasks || []).some((task) =>
        (task.substeps || []).some((substep) => {
          const substepMatches =
            (substep.Description || "").toLowerCase().includes(lowerSearchTerm);

          const issueOrHelpMatches = (substep.issues || []).some((issue) => {
            const issueMatches =
              (issue.Description || "").toLowerCase().includes(lowerSearchTerm);

            const helpMatches = (issue.help || []).some((h) =>
              (h.Description || "").toLowerCase().includes(lowerSearchTerm)
            );

            return issueMatches || helpMatches;
          });

          return substepMatches || issueOrHelpMatches;
        })
      );
    });

    setFilteredTasks(filtered);
  }, [tasks, selectedProcess, searchTerm]);

  const processResultCount = filteredTasks.length;

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-50 via-indigo-50/50 to-sky-50">
      {(user.access === "admin" || user.access === "manager") && <ManagerNav />}
      {user.access === "staff" && <StaffNav />}

      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-indigo-200/70 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 p-4 shadow-[0_12px_30px_rgba(99,102,241,0.16)] backdrop-blur-xl sm:p-6">
          <button
            onClick={goBack}
            className="rounded-xl border border-indigo-200/70 bg-white/85 px-4 py-2 text-sm font-semibold text-indigo-800 shadow-sm hover:bg-white"
          >
            Back
          </button>

          <div className="mt-4 flex items-center gap-4 sm:mt-0">
            <img
              src="tree.png"
              alt="Knowledge Tree"
              className="h-14 w-14 rounded-full border-2 border-indigo-200/70 bg-white shadow-md sm:h-16 sm:w-16"
            />
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-indigo-950 sm:text-3xl">
                Knowledge Tree
              </h1>
              <p className="mt-1 text-sm text-indigo-600">
                Search process knowledge, issues, and proven help steps.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="h-fit rounded-2xl border border-indigo-200/70 bg-white/85 p-4 shadow-[0_12px_24px_rgba(99,102,241,0.12)] backdrop-blur-xl sm:p-5 lg:sticky lg:top-6">
            <h2 className="text-base font-semibold text-indigo-950">Knowledge Filters</h2>
            <p className="mt-1 text-xs text-indigo-600">Refine results by process and keyword.</p>

            <div className="mt-4">
              <label className="mb-2 block text-sm font-semibold text-indigo-900">
                Select Process
              </label>
              <select
                value={selectedProcess}
                onChange={(e) => setSelectedProcess(e.target.value)}
                className="block w-full rounded-xl border border-indigo-200/70 bg-white px-3 py-2 text-sm text-indigo-900 shadow-sm outline-none focus:ring-2 focus:ring-indigo-400"
              >
                <option value="">All Processes</option>
                {processes.map((process, index) => (
                  <option key={index} value={process}>
                    {process}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4">
              <label className="mb-2 block text-sm font-semibold text-indigo-900">
                Search Substeps, Issues, Or Help
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search descriptions, issues, help..."
                className="block w-full rounded-xl border border-indigo-200/70 bg-white px-3 py-2 text-sm text-indigo-900 placeholder-indigo-400 shadow-sm outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            <div className="mt-4 rounded-xl border border-indigo-200/70 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Results</p>
              <p className="mt-1 text-xl font-bold text-indigo-950">{processResultCount}</p>
              <p className="text-xs text-indigo-600">Processes matched</p>
            </div>

            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-indigo-600">
                Quick Process List
              </p>
              <div className="max-h-52 space-y-2 overflow-y-auto pr-1">
                <button
                  onClick={() => setSelectedProcess("")}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm font-medium transition ${
                    selectedProcess === ""
                      ? "border-indigo-300 bg-indigo-600 text-white"
                      : "border-indigo-200/70 bg-white text-indigo-800 hover:bg-indigo-50"
                  }`}
                >
                  All Processes
                </button>
                {processes.map((processName) => (
                  <button
                    key={processName}
                    onClick={() => setSelectedProcess(processName)}
                    className={`w-full rounded-lg border px-3 py-2 text-left text-sm font-medium transition ${
                      selectedProcess === processName
                        ? "border-indigo-300 bg-indigo-600 text-white"
                        : "border-indigo-200/70 bg-white text-indigo-800 hover:bg-indigo-50"
                    }`}
                  >
                    {processName}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <main className="space-y-6">
            {filteredTasks.length === 0 && (
              <div className="rounded-2xl border border-indigo-200/70 bg-white/85 px-6 py-10 text-center shadow-[0_12px_24px_rgba(99,102,241,0.12)] backdrop-blur-xl">
                <p className="text-lg font-semibold text-indigo-900">No Results Found</p>
                <p className="mt-2 text-sm text-indigo-600">Try a different keyword or select another process.</p>
              </div>
            )}

            {filteredTasks.map((process) => (
              <article
                key={process.id}
                className="rounded-2xl border border-indigo-200/70 bg-white/90 p-4 shadow-[0_12px_30px_rgba(99,102,241,0.16)] backdrop-blur-xl sm:p-6"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h2 className="rounded-xl border border-white/20 bg-gradient-to-r from-indigo-900 via-sky-600 to-emerald-500 px-4 py-3 text-xl font-bold text-white shadow-lg shadow-indigo-950/25 sm:text-2xl">
                    {process.Name}
                  </h2>
                  <button
                    onClick={() => navigate("/course", { state: { courseDocName: process.Name } })}
                    className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-950/35 hover:from-indigo-800/95 hover:to-indigo-600/95"
                  >
                    View Documents
                  </button>
                </div>

                <div className="mt-6 space-y-6">
                  {(process.tasks || [])
                    .sort((a, b) => a.Step - b.Step)
                    .map((task) => (
                      <section
                        key={task.id}
                        className="rounded-xl border border-indigo-200/70 bg-white/85 p-4 shadow-sm"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-indigo-200/70 bg-indigo-600 text-base font-bold text-white shadow-sm">
                            {task.Step}
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-indigo-950">{task.TaskName}</h3>
                            <p className="text-sm text-indigo-600">Task Step And Knowledge Blocks</p>
                          </div>
                        </div>

                        <div className="mt-4 space-y-4">
                          {(task.substeps || [])
                            .sort((a, b) => a.Step - b.Step)
                            .map((substep) => (
                              <div
                                key={substep.id}
                                className="rounded-xl border border-indigo-200/70 bg-slate-50 p-4"
                              >
                                <div className="flex items-start gap-3">
                                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-indigo-200/70 bg-white text-sm font-bold text-indigo-800">
                                    {substep.Step}
                                  </div>
                                  <div className="min-w-0">
                                    <h4 className="text-sm font-semibold text-indigo-900">Substep</h4>
                                    <p className="mt-1 text-sm leading-6 text-slate-700">{substep.Description}</p>
                                  </div>
                                </div>

                                <div className="mt-4 space-y-3">
                                  {(substep.issues || []).length === 0 && (
                                    <div className="rounded-lg border border-indigo-200/70 bg-white px-3 py-2 text-xs text-indigo-500">
                                      No known issues logged for this substep.
                                    </div>
                                  )}

                                  {(substep.issues || []).map((issue) => (
                                    <div key={issue.id} className="rounded-xl border border-rose-200/80 bg-rose-50 p-3">
                                      <p className="text-sm font-semibold text-rose-800">Issue</p>
                                      <p className="mt-1 text-sm leading-6 text-rose-900">{issue.Description}</p>

                                      <div className="mt-3 space-y-2">
                                        {(issue.help || []).length > 0 ? (
                                          (issue.help || []).map((helpItem) => (
                                            <div
                                              key={helpItem.id}
                                              className="rounded-lg border border-emerald-200/80 bg-emerald-50 px-3 py-2"
                                            >
                                              <p className="text-sm font-semibold text-emerald-800">Help</p>
                                              <p className="mt-1 text-sm leading-6 text-emerald-900">{helpItem.Description}</p>
                                              <div className="mt-2 flex items-center gap-1" aria-label={`Rating ${helpItem.rating || 0}`}>
                                                {Array.from({ length: helpItem.rating || 0 }).map((_, i) => (
                                                  <StarIcon key={i} className="h-4 w-4 text-amber-500" />
                                                ))}
                                              </div>
                                            </div>
                                          ))
                                        ) : (
                                          <div className="rounded-lg border border-emerald-200/80 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                                            No help notes attached for this issue.
                                          </div>
                                        )}
                                      </div>

                                      {issue.outcome && (
                                        <div className="mt-3 rounded-lg border border-amber-200/80 bg-amber-50 px-3 py-2">
                                          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Outcome</p>
                                          <p className="mt-1 text-sm leading-6 text-amber-900">{issue.outcome}</p>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                        </div>
                      </section>
                    ))}
                </div>
              </article>
            ))}
          </main>
        </div>
      </div>
    </div>
  );
}
